import { storage, getStorageInfo } from '../../../../src/platform/storage.web';
import {
  GAME_PREFS_STORAGE_KEY,
  LEGACY_PWA_V1_KEY,
  LEGACY_PWA_V2_KEY,
  GAME_PREFS_SCHEMA_VERSION,
  createDefaultRootPrefs,
  mergeRootPrefs,
  parseRootPrefs,
  migrateFromLegacyV1,
  type GamePrefs,
  type RootGamePrefs,
} from '@shared/state/gamePrefs';

export type { GamePrefs, RootGamePrefs };
export type ChecklistState = Record<string, boolean>;

export async function loadPwaState(): Promise<RootGamePrefs> {
  try {
    const rawV3 = await storage.getItem(GAME_PREFS_STORAGE_KEY);
    if (rawV3) {
      return parseRootPrefs(JSON.parse(rawV3));
    }

    const rawV2 = await storage.getItem(LEGACY_PWA_V2_KEY);
    if (rawV2) {
      const migrated = parseRootPrefs(JSON.parse(rawV2));
      await storage.setItem(GAME_PREFS_STORAGE_KEY, JSON.stringify(migrated));
      return migrated;
    }

    const rawV1 = await storage.getItem(LEGACY_PWA_V1_KEY);
    if (rawV1) {
      const migrated = migrateFromLegacyV1(JSON.parse(rawV1));
      await storage.setItem(GAME_PREFS_STORAGE_KEY, JSON.stringify(migrated));
      return migrated;
    }
    return createDefaultRootPrefs();
  } catch {
    return createDefaultRootPrefs();
  }
}

export async function savePwaState(state: RootGamePrefs): Promise<void> {
  const next: RootGamePrefs = {
    ...state,
    version: GAME_PREFS_SCHEMA_VERSION,
  };
  await storage.setItem(GAME_PREFS_STORAGE_KEY, JSON.stringify(next));
}

/** Immutable merge helper used by screens/hooks. */
export async function patchGamePrefs(
  gameId: string,
  gamePatch: Partial<GamePrefs>,
  rootPatch: {
    lastGameId?: string | null;
    dismissedUpdatePromptAt?: string | null;
  } = {}
): Promise<RootGamePrefs> {
  const current = await loadPwaState();
  const next = mergeRootPrefs(current, {
    gameId,
    gamePatch,
    ...rootPatch,
  });
  await savePwaState(next);
  return next;
}

export function checklistStateFromPrefs(prefs: GamePrefs): ChecklistState {
  const state: ChecklistState = {};
  for (const id of prefs.completedChecklistIds) {
    state[id] = true;
  }
  return state;
}

export { getStorageInfo, mergeRootPrefs };
